import { useState } from 'react';
import { Users, Calendar, DollarSign, Mail, Phone, Building2, Check, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const eventTypes = [
  { id: 'corporate', label: 'Evento corporativo', icon: Building2 },
  { id: 'party', label: 'Fiesta privada', icon: Users },
  { id: 'wedding', label: 'Boda / Celebración', icon: Calendar },
  { id: 'other', label: 'Otro', icon: DollarSign },
];

const guestRanges = [
  '10-25 personas',
  '25-50 personas',
  '50-100 personas',
  '100-200 personas',
  '200+ personas',
];

const budgetRanges = [
  '$5,000 - $15,000',
  '$15,000 - $30,000',
  '$30,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000+',
];

export default function CateringPage() {
  const [formData, setFormData] = useState({
    eventType: '',
    guests: '',
    date: '',
    budget: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.eventType || !formData.guests || !formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('¡Solicitud enviada!');
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-accent-foreground" />
          </div>
          <h1 className="font-heading text-3xl font-medium">¡Solicitud recibida!</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            Nuestro equipo de catering te contactará en las próximas <strong>24 horas</strong> para discutir los detalles de tu evento.
          </p>
          <div className="mt-8 p-4 bg-secondary/50 rounded-xl">
            <p className="text-sm text-muted-foreground">
              ¿Tienes alguna pregunta urgente?
            </p>
            <p className="font-medium mt-1">catering@lumiere.mx</p>
            <p className="font-medium">81 1234 5678</p>
          </div>
          <Button 
            onClick={() => setIsSubmitted(false)}
            variant="outline"
            className="mt-6 rounded-full"
          >
            Enviar otra solicitud
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-12">
      {/* Hero */}
      <div className="relative bg-primary text-primary-foreground py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Badge className="bg-accent text-accent-foreground mb-4">
            Eventos y grupos
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight">
            Catering para tu evento
          </h1>
          <p className="mt-4 text-primary-foreground/80 text-lg max-w-2xl mx-auto">
            Llevamos la experiencia Lumière a tu evento. Menús personalizados, servicio premium y atención a cada detalle.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Event Type */}
          <div>
            <Label className="text-base font-medium">Tipo de evento *</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              {eventTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, eventType: type.id })}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    formData.eventType === type.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:border-accent/50'
                  }`}
                  data-testid={`event-type-${type.id}`}
                >
                  <type.icon className={`w-6 h-6 mx-auto mb-2 ${
                    formData.eventType === type.id ? 'text-accent' : 'text-muted-foreground'
                  }`} />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Guests and Date */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-base font-medium">Número de invitados *</Label>
              <select
                value={formData.guests}
                onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                className="w-full mt-2 h-12 px-4 bg-background border border-input rounded-xl"
                data-testid="catering-guests-select"
              >
                <option value="">Selecciona rango</option>
                {guestRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-base font-medium">Fecha tentativa</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-2 h-12"
                min={new Date().toISOString().split('T')[0]}
                data-testid="catering-date-input"
              />
            </div>
          </div>

          {/* Budget */}
          <div>
            <Label className="text-base font-medium">Presupuesto estimado</Label>
            <div className="flex flex-wrap gap-2 mt-3">
              {budgetRanges.map(range => (
                <button
                  key={range}
                  type="button"
                  onClick={() => setFormData({ ...formData, budget: range })}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    formData.budget === range
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                  data-testid={`budget-${range}`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="pt-6 border-t border-border">
            <h2 className="font-heading text-xl font-medium mb-6">Información de contacto</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label>Nombre completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tu nombre"
                  className="mt-2 h-12"
                  data-testid="catering-name-input"
                />
              </div>
              <div>
                <Label>Empresa / Organización</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Nombre de empresa"
                  className="mt-2 h-12"
                  data-testid="catering-company-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="mt-2 h-12"
                  data-testid="catering-email-input"
                />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="81 1234 5678"
                  className="mt-2 h-12"
                  data-testid="catering-phone-input"
                />
              </div>
            </div>

            <div className="mt-6">
              <Label>Mensaje / Detalles adicionales</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Cuéntanos más sobre tu evento, preferencias de menú, restricciones alimentarias, etc."
                className="mt-2 min-h-[120px] resize-none"
                data-testid="catering-message-input"
              />
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 rounded-full text-base font-medium"
            data-testid="catering-submit-btn"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Enviando...
              </span>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Enviar solicitud
              </>
            )}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Al enviar, aceptas que nos comuniquemos contigo para dar seguimiento a tu solicitud.
          </p>
        </form>
      </div>

      {/* Features */}
      <div className="bg-secondary/30 py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl font-medium text-center mb-10">
            ¿Por qué elegir nuestro catering?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Menús personalizados', desc: 'Adaptamos cada platillo a tus preferencias y necesidades' },
              { title: 'Servicio profesional', desc: 'Personal capacitado para eventos de cualquier tamaño' },
              { title: 'Calidad garantizada', desc: 'Los mismos ingredientes y estándares de nuestros restaurantes' },
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-medium">{feature.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
